import { adaptReviewToCard } from '@/shared/api/adapters/adaptReviewToCard';

import { doctorAdapter, doctorRecommendationAdapter } from '../adapters/doctor';

import type { IBookingDoctorData } from '@/entities/booking/model/types';
import type { IPagination, IResponseDataList, IStandardResponse } from '@/entities/common/types/api';
import type { IPhoneData } from '@/entities/common/types/phones';
import type { IRecommendationsDoctor } from '@/entities/common/types/recommendations';
import type { IDoctorDetails } from '@/entities/doctor/model/types';
import type { IReview, IReviewStats } from '@/entities/review/model/types';
import type { IServiceDoctor } from '@/entities/service/model/types';

import type { HttpClient } from '../http';
// @todo: убрать эту каку
import type {
  IRecommendationBaseQuery,
  IRecommendationColleaguesQuery,
  IReviewBaseQuery,
} from '@/app/[locale]/[city]/doctor/[alias]/model/types';

class DoctorRepository {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  public async getBookingDataDoctorByAlias(alias: string): Promise<IBookingDoctorData[]> {
    try {
      const data = await this.http.get<IStandardResponse<IBookingDoctorData[]>>(
        `/doctors/${alias}/online-booking-data`,
      );

      return data.data;
    } catch (error) {
      console.error('Error fetching booking data:', error);

      return [];
    }
  }

  public async getPhonesDoctor(medcenterId: number | undefined, doctorId: number | undefined): Promise<IPhoneData> {
    try {
      const response = await this.http.get<IPhoneData>('/phones', {}, { doctorId, medcenterId });

      return response;
    } catch (error) {
      console.error('Error fetching phone data:', error);

      return {
        data: { doctors: null, medcenters: null },
        message: 'Error fetching phone data',
        code: 0,
      };
    }
  }

  public async getDoctorByAlias(alias: string): Promise<IDoctorDetails | null> {
    try {
      const data = await this.http.get<IStandardResponse<IDoctorDetails>>(`/doctors/${alias}`);

      return doctorAdapter(data.data);
    } catch (error) {
      console.error('Error fetching doctor data:', error);

      return null;
    }
  }

  public async getDoctorServicesByAlias(
    alias: string,
    page: number,
  ): Promise<{ services: IServiceDoctor[]; pagination: IPagination }> {
    const defaultPagination = {
      perPage: 0,
      currentPage: page,
      hasNextPage: false,
      total: 0,
    };

    try {
      const response = await this.http.get<IResponseDataList<IServiceDoctor>>(
        `/doctors/${alias}/services`,
        {},
        { page },
      );

      return {
        services: response.data,
        pagination: response.pagination || defaultPagination,
      };
    } catch (error) {
      console.error('Error fetching doctor services:', error);

      return {
        services: [],
        pagination: defaultPagination,
      };
    }
  }

  public async getRecommendationsDoctor(
    query: IRecommendationBaseQuery | IRecommendationColleaguesQuery,
  ): Promise<IRecommendationsDoctor> {
    try {
      const data = await this.http.get<IStandardResponse<IRecommendationsDoctor>>(
        `/doctors/recommendations`,
        {},
        query,
      );

      return doctorRecommendationAdapter(data.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);

      return {
        list: [],
        textData: {
          title: '',
          button: '',
        },
      };
    }
  }

  public async getReviewsDoctor(query: IReviewBaseQuery): Promise<{ review: IReview[]; pagination: IPagination }> {
    const defaultPagination: IPagination = {
      perPage: 0,
      currentPage: query.page,
      hasNextPage: false,
      total: 0,
    };

    try {
      const response = await this.http.get<IResponseDataList<IReview>>(
        `/comments`,
        {},
        {
          type: query.type,
          id: query.id,
          mood: query.mood,
          page: query.page,
        },
      );

      return {
        review: adaptReviewToCard(response.data),
        pagination: response.pagination || defaultPagination,
      };
    } catch (error) {
      console.error('Error fetching reviews:', error);

      return {
        review: [],
        pagination: defaultPagination,
      };
    }
  }

  public async getReviewStats(type: string, id: number): Promise<IReviewStats> {
    try {
      const response = await this.http.get<IStandardResponse<IReviewStats>>(`/comments/stats`, {}, { type, id });

      return response.data;
    } catch (error) {
      console.error('Error fetching review stats:', error);

      return { allCount: 0, positiveCount: 0, negativeCount: 0 };
    }
  }
}

export { DoctorRepository };
